import { useCallback, useEffect, useRef, useState } from 'react';
import { searchCurrencies } from 'services/organization/currency.service';
import { ICurrencySearchItem } from 'types/currency.types';
import CurrencyNameCell from '../../../currenciesTab/components/currencyNameCell/CurrencyNameCell';

import './countryCurrencyDropdown.scss';

interface ICountryCurrencyDropdown {
  countryId?: number;
  selectedCurrencyCode?: string;
  isDisabled?: boolean;
  isUpdating?: boolean;
  onCurrencySelect?: (currency: ICurrencySearchItem) => void;
}

/**
 * Selector compacto de moneda primaria con búsqueda y opciones bandera + nombre.
 */
const CountryCurrencyDropdown = ({
  countryId,
  selectedCurrencyCode,
  isDisabled,
  isUpdating,
  onCurrencySelect
}: ICountryCurrencyDropdown) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currencyOptions, setCurrencyOptions] = useState<ICurrencySearchItem[]>([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const displayCode = selectedCurrencyCode || '—';
  const canInteract = !!countryId && !isDisabled && !isUpdating;

  const closeDropdown = () => {
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleToggle = () => {
    if (!canInteract) return;
    setIsOpen(previous => !previous);
  };

  const handleSelectCurrency = (currency: ICurrencySearchItem) => {
    onCurrencySelect?.(currency);
    closeDropdown();
  };

  const loadCurrencyOptions = useCallback(async (query: string) => {
    setIsLoadingOptions(true);
    try {
      const options = await searchCurrencies(query);
      setCurrencyOptions(options ?? []);
    } catch {
      setCurrencyOptions([]);
    } finally {
      setIsLoadingOptions(false);
    }
  }, []);

  const filteredOptions = currencyOptions.filter(option => {
    if (!searchQuery.trim()) return true;
    const normalizedQuery = searchQuery.trim().toLowerCase();
    return option.code?.toLowerCase().includes(normalizedQuery) || option.name?.toLowerCase().includes(normalizedQuery);
  });

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
      closeDropdown();
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    loadCurrencyOptions('');
  }, [isOpen, loadCurrencyOptions]);

  useEffect(() => {
    if (!isOpen) return;
    window.addEventListener('mousedown', handleClickOutside);

    return () => window.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, handleClickOutside]);

  const renderOptions = () => {
    if (isLoadingOptions) {
      return <div className="dropdownEmptyState">Cargando...</div>;
    }

    if (!filteredOptions.length) {
      return <div className="dropdownEmptyState">Sin resultados</div>;
    }

    return filteredOptions.map(option => (
      <button key={option.id} type="button" className="dropdownOption" onClick={() => handleSelectCurrency(option)}>
        <CurrencyNameCell flag={option.flag} name={option.name} />
      </button>
    ));
  };

  return (
    <div className="countryCurrencyDropdown" ref={containerRef}>
      <div className="labelCountryInput filledLabel">Monedas</div>
      <button
        type="button"
        className={`dropdownTrigger ${canInteract ? 'interactive' : 'disabled'} ${isOpen ? 'open' : ''}`}
        onClick={handleToggle}
        disabled={!canInteract}
      >
        <span className="triggerCode">{isUpdating ? '...' : displayCode}</span>
      </button>
      {isOpen && (
        <div className="dropdownPanel">
          <input
            className="dropdownSearchInput"
            placeholder="Buscar"
            value={searchQuery}
            onChange={event => setSearchQuery(event.target.value)}
          />
          <div className="dropdownOptionsList">{renderOptions()}</div>
        </div>
      )}
    </div>
  );
};

export default CountryCurrencyDropdown;
